import React, { useState, useCallback } from 'react';
import { Menu, MenuItem } from '@mui/material';

import {
  TableRow,
  TableCell,
  Checkbox,
  IconButton,
  Divider,
  MoreVertIcon,
} from '@occc/ui-design-system';
import { RetailerNetworkLocationFragment } from '@occc/graphql';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import {
  AddLocationsToRetailerNetworkLocationGroup,
  RemoveLocationFromRetailerNetwork,
} from '@occc-app/App/PolicyBasedAccessControl/userActions';

type MenuItem = 'addLocationToGroup' | 'removeLocation' | 'locationDetails';

type LocationListItemProps = {
  retailerNetworkUuid: string;
  location: RetailerNetworkLocationFragment;
  isItemSelected: boolean;
  handleItemSelect: (id: string) => void;
  handleOpenAddLocationsToLocationGroupDialog: () => void;
  onRemoveLocationMenuItemClick: (locationUuid: string) => void;
  handleOpenLocationDetailsPage: (locationUuid: string) => void;
};

const LocationListItem = ({
  retailerNetworkUuid,
  location,
  isItemSelected,
  handleItemSelect,
  handleOpenAddLocationsToLocationGroupDialog,
  onRemoveLocationMenuItemClick,
  handleOpenLocationDetailsPage,
}: LocationListItemProps) => {
  const [actionsForUuid, setActionsForUuid] = useState<string | null>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const getHandlerForAdditionalActions =
    (location: RetailerNetworkLocationFragment) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
      setActionsForUuid(location.location.uuid);
    };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setActionsForUuid(null);
  }, []);

  const onCheckboxClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    handleItemSelect(location.location.uuid);
  };

  const onMenuNavigationClick = (itemName: MenuItem) => (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    if (itemName === 'locationDetails') {
      handleOpenLocationDetailsPage(location.location.uuid);
    }

    if (itemName === 'addLocationToGroup') {
      handleItemSelect(location.location.uuid);
      handleOpenAddLocationsToLocationGroupDialog();
    }

    if (itemName === 'removeLocation') {
      onRemoveLocationMenuItemClick(location.location.uuid);
    }

    handleClose();
  };

  return (
    <TableRow
      key={location.uuid}
      selected={isItemSelected}
      onClick={() => handleOpenLocationDetailsPage(location.location.uuid)}
    >
      <Can I={AddLocationsToRetailerNetworkLocationGroup({ uuid: retailerNetworkUuid })}>
        <TableCell padding='checkbox'>
          <Checkbox checked={isItemSelected} onClick={onCheckboxClick} />
        </TableCell>
      </Can>
      <TableCell>{location.location.siteSystemConfiguration[0]?.identifier}</TableCell>
      <TableCell>{location.location.name}</TableCell>
      <TableCell align='right'>
        <IconButton onClick={getHandlerForAdditionalActions(location)} sx={{ p: 0 }}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          open={location.location.uuid === actionsForUuid}
          onClose={(event: MouseEvent) => {
            event.stopPropagation();
            handleClose();
          }}
          anchorEl={anchorEl}
          PaperProps={{
            sx: {
              borderRadius: '4px',
            },
          }}
        >
          <MenuItem onClick={onMenuNavigationClick('locationDetails')}>
            View Location Details
          </MenuItem>

          <Can I={AddLocationsToRetailerNetworkLocationGroup({ uuid: retailerNetworkUuid })}>
            <MenuItem onClick={onMenuNavigationClick('addLocationToGroup')}>
              Add to Location Group
            </MenuItem>
          </Can>

          <Can I={RemoveLocationFromRetailerNetwork({ uuid: retailerNetworkUuid })}>
            <Divider />
            <MenuItem onClick={onMenuNavigationClick('removeLocation')}>
              Remove from Retailer Network
            </MenuItem>
          </Can>
        </Menu>
      </TableCell>
    </TableRow>
  );
};

export default LocationListItem;
